export type ImportStatus =
    | "pending"
    | "processing"
    | "completed"
    | "completed_with_errors"
    | "failed"
    | string

export type ImportMessage = {
    at?: string | null
    level?: string | null
    sheet?: string | null
    entity?: string | null
    row?: number | string | null
    identifier?: string | null
    message?: string | null
    meta?: Record<string, any> | null
}

export type ImportRow = {
    id: string
    status: ImportStatus
    created_at?: string | null
    updated_at?: string | null
    file_name?: string | null
}

export type Import = ImportRow & {
    messages?: ImportMessage[] | null
}

export const statusBadgeClass = (status: string) => {
    switch (status) {
        case "completed":
            return "badge bg-success"
        case "completed_with_errors":
            return "badge bg-warning text-dark"
        case "failed":
            return "badge bg-danger"
        case "processing":
            return "badge bg-primary"
        case "pending":
        default:
            return "badge bg-secondary"
    }
}

export const displayName = (imp: Pick<ImportRow, "id" | "file_name">) => {
    const n = (imp.file_name || "").trim()
    return n || imp.id
}

export const normalizeImports = (data: any): ImportRow[] => {
    if (!data) return []
    if (Array.isArray(data)) return data as ImportRow[]
    if (typeof data === "object" && Array.isArray((data as any).imports)) return (data as any).imports as ImportRow[]
    return []
}

export const normalizeMessages = (messages: any): ImportMessage[] => {
    if (!messages) return []
    if (Array.isArray(messages)) return messages.filter((m) => m && typeof m === "object") as ImportMessage[]
    if (typeof messages === "object")
        return Object.values(messages).filter((m) => m && typeof m === "object") as ImportMessage[]
    return []
}

export const niceDate = (iso?: string | null) => {
    if (!iso) return "—"
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return "—"
    return d.toLocaleString()
}

// ---- messages helpers ----
export const isError = (m: ImportMessage) => (m.level || "").toLowerCase() === "error"

export const metaObj = (m: ImportMessage): Record<string, any> | null => {
    const meta = m?.meta
    return meta && typeof meta === "object" ? meta : null
}

export const lastRunId = (messages: ImportMessage[]): string | null => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const meta = metaObj(messages[i])
        if (!meta) continue
        if (String(meta.action || "") === "run_started" && meta.run_id) return String(meta.run_id)
    }
    return null
}

export const filterToLastRun = (messages: ImportMessage[]) => {
    const runId = lastRunId(messages)
    if (!runId) return { runId: null as string | null, messages }

    return {
        runId,
        messages: messages.filter((m) => String(metaObj(m)?.run_id || "") === runId),
    }
}

export const lastRunAt = (messages: ImportMessage[]): string | null => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const meta = metaObj(messages[i])
        if (!meta) continue
        if (String(meta.action || "") === "run_started") return messages[i]?.at || null
    }
    return null
}

export const extractCreatedCounts = (messages: ImportMessage[]) => {
    const created: Record<string, number> = {}

    for (const m of messages) {
        const meta = metaObj(m)
        if (!meta) continue
        if (String(meta.action ?? "").toLowerCase() !== "created") continue

        const model = String(meta.model ?? "").trim()
        if (!model) continue
        created[model] = (created[model] || 0) + 1
    }

    const total = Object.values(created).reduce((sum, n) => sum + n, 0)
    const entries = Object.entries(created)
        .sort((a, b) => b[1] - a[1])
        .map(([model, count]) => ({ model, count }))

    return { total, entries }
}

export const filterByQuery = (list: ImportMessage[], q: string) => {
    const qq = q.trim().toLowerCase()
    if (!qq) return list

    return list.filter((m) => {
        const hay = [m.sheet, m.entity, m.identifier, m.message, String(m.row ?? "")]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
        return hay.includes(qq)
    })
}

// ---- api helpers ----
const jsonOrEmpty = async (r: Response) => r.json().catch(() => ({}))

export const uploadImport = async (args: {
    url: string
    file: File
}): Promise<{ ok: true; id: string } | { ok: false; error: string }> => {
    const form = new FormData()
    form.append("import[file]", args.file)

    const response = await fetch(args.url, { method: "POST", headers: { Accept: "application/json" }, body: form })
    const result = await jsonOrEmpty(response)

    if (response.ok && result?.import?.id) return { ok: true, id: String(result.import.id) }
    return { ok: false, error: result?.errors?.join(", ") || result?.error || "Please try again" }
}

export const postImportAction = async (args: {
    url: string
}): Promise<{ ok: true } | { ok: false; error: string }> => {
    const response = await fetch(args.url, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({}),
    })
    const result = await jsonOrEmpty(response)
    if (response.ok) return { ok: true }
    return { ok: false, error: result?.error || "Please try again" }
}
